import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import classnames from 'classnames';
import InfiniteScroll from 'react-infinite-scroller';
import { IconButton } from '@nike/nike-design-system-components';
import { Download, Data, Journal } from '@nike/nike-design-system-icons';

import OrderStatusCard from './OrderStatusCard';
import DateRangeSelector from '../DateRangeSelector';
import { DownloadOrderDetailsReport } from '../downloadOrderDetailsReport';
import { ArchiveOrdersModal } from '../archiveOrders';

import { dateRangeOptions, searchKeyOptions, sortOptions } from './selectOptions';
import { Button, NoResults, Spinner } from '../../components/';
import TableInfiniteScroll from '~COMPONENTS/table/TableInfiniteScroll';
import SortOptions from '~COMPONENTS/sortOptions/SortOptions';
import LoadingMessage from '~COMPONENTS/spinner/LoadingMessage';
import StatusSearch from '~COMPONENTS/formInputs/StatusSearch';
import PageHeader from '~COMPONENTS/containers/PageHeader';
import { DownloadBanner } from '../../components/DownloadBanner';
import { Filters } from '../../shop/filters/';
import { TranslatedCopy } from '../../intl/TranslatedCopy';
import { showToast } from '../../components/toast/actions';

import {
  addOrdersFilter,
  clearOrdersFilters,
  clearSortBy,
  removeOrdersFilter,
  requestOrders,
  resetPreviousLocation,
  setOrdersFilters,
  updateOrdersDateRange,
  updateSortBy,
  setOrdersTableSearch,
  clearOrdersTableSearch,
  generateOrderTrackingReport,
  clearDownloadReportError,
  archiveOrders
} from '../actions';
import {
  dateRange,
  filterOptions,
  isFetching,
  previousLocationWasOrderDetails,
  previousLocationWasReturnStatus,
  previousLocationWasReturnStep,
  selectedFilters,
  sortBy,
  ordersTableSearch,
  isGeneratingOrderTrackingReport,
  hasDownloadError
} from '../reducer';
import { setOriginatesFromReturnStatus } from '../orderReturns/actions';
import { setSearchTerm as setGlobalSearchTerm, setSortBy } from '../../global/actions';
import { searchTerm as globalSearchTerm, sortBy as globalSortBy } from '../../global/reducer';
import { hasAuthCheckFinished } from '../../auth/reducer';
import { userDepartments } from '../../user/reducer';
import { billableDepartments, getUserAccount, selectDepartments, getUserData } from '../../user/selectors';
import { navNewReturn } from '../returnStatus/reducer';
import { setNavNewReturn } from '../returnStatus/actions';

import columns from './columns';
import { findUnique } from '../../util/findUnique';

import { dateRangeTypes, filterOptionTypes, sortByTypes } from '../../types/status';
import {
  departmentsTypes,
  userAccountTypes,
  userDepartmentTypes,
  gamedayAccountTypes,
  userTypes
} from '../../types/user';

import {
  DEFAULT_DATE_RANGE,
  LABEL_SELECT_ALL,
  ORDER_TYPE_ATONCE,
  ORDER_TYPE_ATONCE_ALT,
  ORDER_TYPE_FUTURES,
  ORDER_TYPE_FORECAST
} from '../../shop/constants';
import { STATUS_SHIPPED, STATUS_PARTIALLY_SHIPPED } from '../orderReturns/constants';
import { ORDERTYPE_CODE_MAP } from '../constants';

import { mapDepartmentName, mapDepartmentNumbers, updateAllFilters } from '../status/util';
import { getCurrentAllotments, getGamedayAccount, getIsGreaterChinaSalesOrg } from '../../gamedayAccount/selectors';
import { canAccessSystemManagement } from '../../util/rolePermissions';

const DEFAULT_SEARCH_KEY = 'sapOrderNumbers';
const PAGE = 'ORDER_STATUS';

export class OrderStatus extends Component {
  constructor(props) {
    super(props);
    const {
      navNewReturn,
      globalSearchTerm,
      globalSortBy,
      globalSortBy: { field, order },
      ordersTableSearch: { searchKey, searchTerm }
    } = this.props;
    this.state = {
      initialLoad: true,
      requestQueue: 0,
      isFilterOpen: false,
      ordersList: [],
      searchKey: searchKey || DEFAULT_SEARCH_KEY,
      searchTerm: searchTerm || (globalSearchTerm && globalSearchTerm !== '' ? globalSearchTerm : ''),
      showSearchResultsStats: !!(
        (globalSearchTerm && globalSearchTerm !== '') ||
        (globalSortBy && field !== '' && order !== '')
      ),
      isOrderNumberSearch: false,
      departments: [],
      selectAllToggle: false,
      hasMore: true,
      page: 1,
      paginate: false,
      paramCache: null,
      selectForReturn: navNewReturn,
      isDownloadReportModalOpen: false,
      isArchiveConfirmModalOpen: false,
      isArchiving: false,
      orderIdsSelectedMap: {},
      showDownloadBanner: false
    };
  }

  async componentDidMount() {
    const { selectForReturn } = this.state;
    const {
      previousLocationWasOrderDetails,
      previousLocationWasReturnStatus,
      previousLocationWasReturnStep,
      setNavNewReturn,
      setOriginatesFromReturnStatus,
      userAccount,
      ordersTableSearch: { searchKey, searchTerm }
    } = this.props;

    if (!userAccount || !userAccount.number) {
      return;
    }

    await this.getDepartments();
    if (previousLocationWasOrderDetails || previousLocationWasReturnStep || selectForReturn) {
      await this.filterOrdersList();
    } else {
      await this.clearSearchAndFilters(true);
      await this.fetchOrderRequest();
    }
    this.setFilterOptions();
    if ((!searchKey && !searchTerm) || !previousLocationWasOrderDetails) {
      this.resetForm();
    }
    setOriginatesFromReturnStatus(previousLocationWasReturnStatus);
    setNavNewReturn(false);
  }

  async componentDidUpdate(prevProps, prevState) {
    const { gamedayAccount, hasAuthCheckFinished, billableDepartments } = this.props;
    const { requestQueue } = this.state;

    if (
      hasAuthCheckFinished &&
      gamedayAccount &&
      prevProps.gamedayAccount.accountNumber !== gamedayAccount.accountNumber
    ) {
      // User switched Sold-to
      await this.getDepartments();
      await this.clearSearchAndFilters();
      this.setFilterOptions();
      this.fetchOrderRequest();
      this.setState({ orderIdsSelectedMap: {} });
    } else if (prevState.requestQueue !== requestQueue && requestQueue === 0) {
      this.filterOrdersList();
    }
    if (prevProps.billableDepartments.length === 0 && billableDepartments.length > 0) this.fetchOrderRequest();
  }

  componentWillUnmount() {
    const { setGlobalSearchTerm, setSortBy, sortBy } = this.props;
    setGlobalSearchTerm('');
    setSortBy({ ...sortBy, page: PAGE });
  }

  setSearchTerm = val => this.setState({ searchTerm: val });

  setSearchKey = async ({ value }) => {
    await this.clearSearchAndFilters();

    const { change } = this.props;
    change('orderStatus__search__input', '');
    change('orderStatus__search__searchKey', value);
    this.setState({ searchKey: value, searchTerm: '' });
  };

  setOrdersList = (orders, count) => {
    const { ordersList, page } = this.state;
    const recordsCount = count || 0;

    this.setState({
      ordersList: ordersList && page > 1 ? [...ordersList, ...orders] : orders,
      totalRecords: recordsCount,
      initialLoad: false
    });
  };

  getDepartments = () => {
    const {
      userDepartments: { deptNumbers, depts }
    } = this.props;
    return this.setState({
      soldToDepartmentNumbers: deptNumbers,
      departments: depts
    });
  };

  setFilterOptions = () => {
    const {
      globalSortBy: { field, order },
      updateSortBy,
      setOrdersFilters,
      userDepartments: { deptNames }
    } = this.props;
    if (field !== '' && order !== '') updateSortBy({ field: field, order: order });
    setOrdersFilters({ key: 'departmentNames', values: deptNames });
  };

  getOrderDetails = id => {
    const { history } = this.props;
    history.push(`/orders/id/${id}`);
  };

  incrementRequestQueue = () => this.setState(prevState => ({ requestQueue: prevState.requestQueue + 1 }));

  handleFilterClick = () => {
    this.setState(prevState => ({ isFilterOpen: !prevState.isFilterOpen }));
  };

  handleListSort = async ({ value, desc }) => {
    const { updateSortBy } = this.props;
    const isDesc = desc ? 'DESC' : 'ASC';
    await updateSortBy({ field: value, order: isDesc });
    this.filterOrdersList();
  };

  handleSearchRequest = () => {
    const { searchKey, searchTerm } = this.state;
    this.fetchOrderRequest({ [searchKey]: [searchTerm] });
  };

  sortOptionsRef = React.createRef();

  sortOptionsRefDesktop = React.createRef();

  fetchOrderRequest = (additionalParams = {}, isLastInQueue = false) => {
    const { isFetching, filterOptions } = this.props;
    const { requestQueue } = this.state;
    let { paramCache } = this.state;
    if (additionalParams && Object.keys(additionalParams).length === 0) paramCache = {};

    if (!isLastInQueue && isFetching) {
      this.incrementRequestQueue();
    } else if (requestQueue === 0) {
      const { soldToDepartmentNumbers } = this.state;
      const {
        sortBy,
        dateRange,
        userAccount: { number }
      } = this.props;

      paramCache = {
        accountNumber: number,
        departments: soldToDepartmentNumbers,
        orderTypes: filterOptions.filter(({ key }) => key === 'orderType'),
        dateRange:
          dateRange && dateRange.startDate && dateRange.endDate
            ? {
                startDate: dateRange.startDate.replace(/\//g, '-'),
                endDate: dateRange.endDate.replace(/\//g, '-'),
                period: 'CUSTOM'
              }
            : { ...dateRange, period: dateRange.period.toUpperCase() },
        isArchived: false,
        sort: sortBy
          ? {
              ...sortBy,
              field: sortBy.field === 'dateSubmitted' ? 'createTimestamp' : sortBy.field
            }
          : null,
        page: {
          number: 1,
          size: 10
        },
        ...additionalParams
      };
      if (paramCache.orderTypes && paramCache.orderTypes.length > 0) {
        // If at-once found, include additional at-once code
        if (typeof paramCache.orderTypes[0] === 'object' && paramCache.orderTypes[0].values.length) {
          paramCache.orderTypes = paramCache.orderTypes[0].values.map(type => type.value);
        }
        if (paramCache.orderTypes.filter(type => type === ORDERTYPE_CODE_MAP[ORDER_TYPE_ATONCE]).length > 0) {
          paramCache.orderTypes.push(ORDERTYPE_CODE_MAP[ORDER_TYPE_ATONCE_ALT]);
        }
      } else {
        paramCache.orderTypes = [
          ORDERTYPE_CODE_MAP[ORDER_TYPE_ATONCE],
          ORDERTYPE_CODE_MAP[ORDER_TYPE_ATONCE_ALT],
          ORDERTYPE_CODE_MAP[ORDER_TYPE_FUTURES],
          ORDERTYPE_CODE_MAP[ORDER_TYPE_FORECAST]
        ];
      }
      this.setState({ paramCache }, () => this.fetchOrders());
    }
  };

  fetchOrders = async () => {
    const { paramCache, initialLoad } = this.state;
    const {
      requestOrders,
      userAllDepartments,
      billableDepartments,
      userAccount: { number }
    } = this.props;

    return requestOrders(paramCache).then(async ({ payload: { orders, totalRecords } }) => {
      const mappedOrders =
        orders && orders.length
          ? await mapDepartmentName(
              orders,
              [...billableDepartments, ...userAllDepartments],
              billableDepartments.length > 0 ? [number, billableDepartments[0].customerNumber] : [number]
            )
          : orders;
      this.setState({
        hasMore: mappedOrders && mappedOrders.length ? mappedOrders.length === 10 : false,
        paginate: false,
        requestQueue: 0
      });
      if (initialLoad) this.setFilterOptions();
      return this.setOrdersList(mappedOrders || [], totalRecords || 0);
    });
  };

  filterOrdersList = async () => {
    const { departments, searchTerm, searchKey, paginate, page, selectForReturn } = this.state;
    const { selectedFilters } = this.props;
    const additionalParams = {
      page: {
        number: 1,
        size: 10
      }
    };
    const departmentNamesList = Array.from(selectedFilters.get('departmentNames'));
    const orderType = Array.from(selectedFilters.get('orderType'));
    const orderStatus = Array.from(selectedFilters.get('orderStatus'));
    const paymentMethod = Array.from(selectedFilters.get('paymentMethod'));

    if (departmentNamesList.length) {
      additionalParams.departments = mapDepartmentNumbers(departmentNamesList, departments);
    }
    if (orderType.length) {
      additionalParams.orderTypes = orderType;
    }
    if (orderStatus.length) {
      additionalParams.orderStatuses = orderStatus;
    }
    if (selectForReturn) {
      additionalParams.orderStatuses = [STATUS_SHIPPED, STATUS_PARTIALLY_SHIPPED];
    }
    if (paymentMethod.length) {
      additionalParams.paymentMethods = paymentMethod;
    }
    if (searchTerm && searchKey) {
      // Can accept multiple comma delimited values... setting with one for now
      additionalParams[searchKey] = [searchTerm];
    }
    this.setState({
      requestQueue: 0
    });
    if (paginate) {
      additionalParams.page.number = page;
    } else {
      this.setState({ page: 1 });
    }

    return this.fetchOrderRequest(additionalParams);
  };

  selectOptions = key => {
    const { ordersList } = this.state;
    return { key: key, values: findUnique(ordersList || [], key) };
  };

  handleSelection = async (name, isChecked) => {
    const { selectAllToggle } = this.state;
    const { addOrdersFilter, removeOrdersFilter, filterOptions } = this.props;
    // --
    const [key, value] = name.split('--');

    await this.resetPage();
    this.incrementRequestQueue();

    if (name === LABEL_SELECT_ALL) {
      await updateAllFilters(addOrdersFilter, removeOrdersFilter, filterOptions, 'departmentNames', !selectAllToggle);
      this.setState({ selectAllToggle: !selectAllToggle });
      this.filterOrdersList();
      return;
    }
    if (isChecked) {
      this.setState({ showSearchResultsStats: true });
      await addOrdersFilter({ key, value });
    } else {
      this.setState({ selectAllToggle: false });
      await removeOrdersFilter({ key, value });
    }
    this.filterOrdersList(value, key);
  };

  clearSearch = async () => {
    const { searchTerm } = this.state;
    const { clearOrdersTableSearch } = this.props;
    if (searchTerm) {
      await this.setState({ searchTerm: '', showSearchResultsStats: false, isOrderNumberSearch: false, page: 1 });
    }
    await clearOrdersTableSearch();
    return this.filterOrdersList();
  };

  clearOnlyFilters = async () => {
    const { clearOrdersFilters, resetPreviousLocation } = this.props;
    await clearOrdersFilters();
    await resetPreviousLocation();
    this.setState(
      {
        page: 1
      },
      () => {
        this.fetchOrderRequest();
      }
    );
  };

  clearSearchAndFilters = async onMount => {
    const {
      clearOrdersFilters,
      clearSortBy,
      clearOrdersTableSearch,
      updateOrdersDateRange,
      resetPreviousLocation
    } = this.props;
    const { initialLoad } = this.state;
    await clearOrdersFilters();
    await clearSortBy();
    await clearOrdersTableSearch();
    if (onMount !== true) {
      await resetPreviousLocation();
    }

    this.resetForm();
    if (
      this.sortOptionsRef &&
      this.sortOptionsRef.current &&
      this.sortOptionsRefDesktop &&
      this.sortOptionsRefDesktop.current
    ) {
      this.sortOptionsRef.current.resetOptions();
      this.sortOptionsRefDesktop.current.resetOptions();
    }

    updateOrdersDateRange({
      period: DEFAULT_DATE_RANGE,
      startDate: null,
      endDate: null
    });

    await this.setState({
      searchTerm: '',
      searchKey: DEFAULT_SEARCH_KEY,
      showSearchResultsStats: false,
      isOrderNumberSearch: false,
      page: 1
    });
    if (!initialLoad) {
      this.fetchOrderRequest();
    }
  };

  handleDateChange = dateRange => {
    const { updateOrdersDateRange } = this.props;
    this.setState({ showSearchResultsStats: dateRange.period !== DEFAULT_DATE_RANGE });
    updateOrdersDateRange(dateRange);
  };

  submitSearch = async () => {
    const { searchKey, searchTerm } = this.state;
    const { setOrdersTableSearch } = this.props;
    if (searchTerm.length < 3) {
      return null;
    }
    const searchParams = { [searchKey]: [searchTerm] };

    setOrdersTableSearch({ searchKey, searchTerm });

    if (searchKey === DEFAULT_SEARCH_KEY && searchTerm) {
      return this.setState({ showSearchResultsStats: true, page: 1, isOrderNumberSearch: true }, () =>
        this.fetchOrderRequest(searchParams)
      );
    }
    return this.setState({ showSearchResultsStats: true, page: 1, isOrderNumberSearch: false }, () =>
      this.filterOrdersList()
    );
  };

  loadInfiniteScrollOrders = () => {
    // Limiting issues around multiple calls (one for table and one for cards)
    // Not my favorite solve, but I couldn't seem to limit with a debounce
    setTimeout(() => {
      const { isFetching } = this.props;
      const { page, hasMore } = this.state;
      if (hasMore && !isFetching) {
        this.setState({ page: page + 1, hasMore: true, paginate: true }, () => this.filterOrdersList());
      }
    }, 250);
  };

  resetPage = () => this.setState({ page: 1 });

  resetForm = () => {
    const { change } = this.props;
    change('orderStatus__search__input', '');
    change('orderStatus__search__searchKey', DEFAULT_SEARCH_KEY);
  };

  handleRowSelect = (id, isChecked) => {
    const { orderIdsSelectedMap } = this.state;
    orderIdsSelectedMap[id] = isChecked;
    this.setState({ orderIdsSelectedMap });
  };

  handleDownloadOrderTrackingReport = async () => {
    const { generateOrderTrackingReport } = this.props;
    const { orderIdsSelectedMap } = this.state;
    this.setState({ showDownloadBanner: true });
    const orderIds = Object.keys(orderIdsSelectedMap).filter(id => orderIdsSelectedMap[id]);
    await generateOrderTrackingReport({ orderIds });
  };

  handleOpenArchiveConfirmModal = () => {
    this.setState({ isArchiveConfirmModalOpen: true });
  };

  handleArchiveOrders = async () => {
    const { archiveOrders, showToast } = this.props;
    const { orderIdsSelectedMap } = this.state;
    const orderIds = Object.keys(orderIdsSelectedMap).filter(id => orderIdsSelectedMap[id]);

    this.setState({ isArchiving: true });

    const result = await archiveOrders({ orderIds });

    if (result.request?.error) {
      this.setState({
        isArchiveConfirmModalOpen: false,
        isArchiving: false
      });
      showToast({
        type: 'error',
        message: 'Failed to archive orders. Please try again.',
        error: result.request.error
      });
      return;
    }

    this.setState({
      isArchiveConfirmModalOpen: false,
      isArchiving: false,
      orderIdsSelectedMap: {}
    });
    await this.filterOrdersList(); // reload table
    showToast({
      type: 'success',
      message: 'Successfully archived orders'
    });
  };

  handleCloseBanner = async () => {
    const { clearDownloadReportError } = this.props;
    this.setState({ showDownloadBanner: false });
    await clearDownloadReportError();
  };

  render() {
    const {
      hasMore,
      isFilterOpen,
      isOrderNumberSearch,
      ordersList,
      paginate,
      selectAllToggle,
      showSearchResultsStats,
      totalRecords,
      selectForReturn,
      isDownloadReportModalOpen,
      isArchiveConfirmModalOpen,
      isArchiving,
      paramCache,
      orderIdsSelectedMap,
      showDownloadBanner
    } = this.state;
    const {
      change,
      currencyCode,
      dateRange,
      filterOptions,
      handleSubmit,
      isFetching,
      selectedFilters,
      sortBy,
      globalSortBy,
      isGreaterChinaSalesOrg,
      isGeneratingOrderTrackingReport,
      hasDownloadError,
      user
    } = this.props;
    const defaultSortBy =
      globalSortBy && globalSortBy.page === PAGE && globalSortBy.field !== '' && globalSortBy.order !== ''
        ? {
            value: globalSortBy.field,
            name: new TranslatedCopy({ id: `orderstatus.${globalSortBy.field}` }).toString(),
            desc: globalSortBy.order === 'DESC'
          }
        : {
            value: sortBy.field,
            name: new TranslatedCopy({ id: `orderstatus.${sortBy.field}` }).toString(),
            desc: sortBy.order === 'DESC'
          };
    const ordersListWithCurrency = ordersList.map(order => ({ ...order, currencyCode: currencyCode || 'USD' }));
    return (
      <>
        <div className='orderStatus__main_outer'>
          <div className='status__main_outerWrapper'>
            <PageHeader
              page='orderStatus'
              title={
                <>
                  <span className='mr5-lg'>
                    <TranslatedCopy id='global.orders' />
                  </span>
                  {!isFetching && (
                    <div className='d-sm-b d-md-ib'>
                      <div className='body-4 flex-container row center mt1-sm'>
                        <div className='u-bold mr3-sm' data-desc='orderStatus__search_count'>
                          <TranslatedCopy id='orderstatus.header.result' values={{ amount: totalRecords || 0 }} />
                        </div>
                        {showSearchResultsStats && (
                          <Button
                            className='text-color-secondary body-4 p0-sm text-underline fl-sm-r'
                            data-desc='orderStatus__search_clear'
                            useBareClass
                            textLink
                            onClick={this.clearSearchAndFilters}
                          >
                            <TranslatedCopy id='orderstatus.search.clear' />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              }
              className='flex-container row bg-white align-center'
            >
              <div className='flex-container row align-center' style={{ justifyContent: 'flex-end' }}>
                <div className='status__search_container_desktop d-sm-h d-lg-ib'>
                  <StatusSearch
                    onSubmit={e => handleSubmit(this.submitSearch(e))}
                    name='orderStatus__search'
                    clearSearch={this.clearSearch}
                    searchKeyOptions={searchKeyOptions}
                    setSearchTerm={this.setSearchTerm}
                    setSearchKey={this.setSearchKey}
                    change={change}
                  />
                </div>
              </div>
            </PageHeader>
            <div className='status__search_container_mobile d-lg-h bg-white pt4-sm prl3-sm'>
              <StatusSearch
                onSubmit={e => handleSubmit(this.submitSearch(e))}
                name='orderStatus__search'
                clearSearch={this.clearSearch}
                searchKeyOptions={searchKeyOptions}
                setSearchTerm={this.setSearchTerm}
                setSearchKey={this.setSearchKey}
                change={change}
              />
            </div>
            <div className='d-sm-b d-lg-h'>
              <div className='flex-container row space-between bg-white p4-sm'>
                <Button onClick={this.handleFilterClick}>
                  <TranslatedCopy id='global.filter' />
                </Button>
                <SortOptions
                  ref={this.sortOptionsRef}
                  list={ordersList}
                  sortBy={sortOptions}
                  defaultValue={defaultSortBy}
                  handleExternalSort={this.handleListSort}
                />
              </div>
            </div>
            {!isOrderNumberSearch && (
              <div
                className={classnames('status__filters order-status-item pl8-md pr4-md flex-container', {
                  isOpen: isFilterOpen
                })}
              >
                <Filters
                  filters={filterOptions}
                  onChange={this.handleSelection}
                  selected={selectedFilters}
                  selectAll={selectAllToggle}
                  pageName='orderstatus'
                  showFilterCount
                  closeOnOutsideClick
                />
                <DateRangeSelector
                  dateRange={dateRange}
                  dateRangeOptions={dateRangeOptions}
                  updateDateRange={this.handleDateChange}
                  resetPage={this.resetPage}
                  filterList={this.filterOrdersList}
                />
                <div className='d-sm-h d-lg-b flex-expand'>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', height: '100%', alignItems: 'center' }}>
                    {Object.values(orderIdsSelectedMap).some(o => o) ? (
                      <>
                        {canAccessSystemManagement(user.role) && (
                          <IconButton
                            size='small'
                            appearance='secondary'
                            Icon={<Journal aria-hidden />}
                            style={{
                              backgroundColor: 'transparent',
                              outline: 'none',
                              marginRight: '10px',
                              height: '40px'
                            }}
                            onClick={this.handleOpenArchiveConfirmModal}
                            ariaLabel='archiveOrders'
                            title='Archive Orders'
                          />
                        )}
                        <IconButton
                          size='small'
                          appearance='secondary'
                          Icon={<Data aria-hidden />}
                          style={{
                            backgroundColor: 'transparent',
                            outline: 'none',
                            marginRight: '10px',
                            height: '40px'
                          }}
                          onClick={this.handleDownloadOrderTrackingReport}
                          ariaLabel='downloadOrderTrackingReport'
                          title={new TranslatedCopy({ id: 'orderstatus.download.orderTrackingReport' }).toString()}
                        />
                      </>
                    ) : null}
                    <IconButton
                      size='small'
                      appearance='secondary'
                      Icon={<Download aria-hidden title='Download Order Details Report' />}
                      style={{ backgroundColor: 'transparent', outline: 'none', marginRight: '10px', height: '40px' }}
                      onClick={() => this.setState({ isDownloadReportModalOpen: true })}
                      ariaLabel='downloadOrderDetails'
                    />
                    <SortOptions
                      ref={this.sortOptionsRefDesktop}
                      list={ordersList}
                      sortBy={sortOptions}
                      defaultValue={defaultSortBy}
                      handleExternalSort={this.handleListSort}
                    />
                  </div>
                </div>
              </div>
            )}

            {selectForReturn && (
              <div className='p7-sm pl12-sm u-full-width bg-black'>
                <p className='u-bold text-color-primary-light'>
                  <TranslatedCopy id='orderstatus.header.returnSelect' />
                </p>
              </div>
            )}

            <div className='status__filters_bg' onClick={this.handleFilterClick} />
            <div className='p5-sm'>
              {isFetching ? (
                <LoadingMessage title={new TranslatedCopy({ id: 'global.results' }).toString()} />
              ) : (
                <>
                  {(!ordersList || !ordersList.length) && !isOrderNumberSearch && (
                    <NoResults titleId='orderstatus.search.order.noresults.copy3'>
                      <div className='status__search_noResults'>
                        <p className='mt5-sm mb3-sm'>
                          <TranslatedCopy id='orderstatus.search.noresults.copy' />
                        </p>
                      </div>
                    </NoResults>
                  )}
                  {(!ordersList || !ordersList.length) && showSearchResultsStats && isOrderNumberSearch && (
                    <NoResults titleId='orderstatus.search.order.noresults.title'>
                      <div className='status__search_noResults'>
                        <p className='mt5-sm mb3-sm'>
                          <TranslatedCopy id='orderstatus.search.order.noresults.copy1' />
                        </p>
                        <p>
                          <TranslatedCopy id='orderstatus.search.order.noresults.copy2' />
                        </p>
                      </div>
                    </NoResults>
                  )}
                </>
              )}
              {((ordersList && !!ordersList.length && !isFetching) || (paginate && isFetching)) && (
                <>
                  <div className='table-view body-4 pb4-md d-sm-h d-lg-b orderStatus-infinite-scroll'>
                    <TableInfiniteScroll
                      data={ordersListWithCurrency}
                      columns={columns}
                      loadMore={this.loadInfiniteScrollOrders}
                      hasMore={hasMore}
                      clickTarget='id'
                      handleClick={this.getOrderDetails}
                      includeRowSelect
                      rowSelectAction={this.handleRowSelect}
                      selectedIdMap={orderIdsSelectedMap}
                    />
                  </div>
                  <InfiniteScroll
                    element='div'
                    className='d-lg-h'
                    pageStart={0}
                    loadMore={this.loadInfiniteScrollOrders}
                    hasMore={hasMore}
                    loader={
                      <div key='orderStatus__loader' className='mt4-sm'>
                        <Spinner />
                      </div>
                    }
                    initialLoad={false}
                    threshold={250}
                  >
                    {ordersListWithCurrency.map((order, idx) => (
                      <OrderStatusCard
                        // eslint-disable-next-line react/no-array-index-key
                        key={`orderStatusCard__${order.id}__${idx}`}
                        order={order}
                        handleClick={e => this.getOrderDetails(e)}
                        isGreaterChinaSalesOrg={isGreaterChinaSalesOrg}
                      />
                    ))}
                  </InfiniteScroll>
                </>
              )}
            </div>
          </div>
        </div>
        <DownloadOrderDetailsReport
          isOpen={isDownloadReportModalOpen}
          closeModal={() => this.setState({ isDownloadReportModalOpen: false })}
          accountNumber={paramCache?.accountNumber}
          departments={paramCache?.departments}
          sort={paramCache?.sort}
        />
        {showDownloadBanner && (
          <DownloadBanner
            title={new TranslatedCopy({ id: 'orderstatus.orderTrackingReport' }).toString()}
            isLoading={isGeneratingOrderTrackingReport}
            hasError={hasDownloadError}
            closeBanner={this.handleCloseBanner}
          />
        )}
        {isArchiveConfirmModalOpen && (
          <ArchiveOrdersModal
            isOpen={isArchiveConfirmModalOpen}
            closeModal={() => this.setState({ isArchiveConfirmModalOpen: false })}
            orderIds={Object.keys(orderIdsSelectedMap).filter(id => orderIdsSelectedMap[id])}
            onConfirm={this.handleArchiveOrders}
            isLoading={isArchiving}
          />
        )}
      </>
    );
  }
}

OrderStatus.propTypes = {
  addOrdersFilter: PropTypes.func.isRequired,
  billableDepartments: departmentsTypes,
  change: PropTypes.func.isRequired,
  clearOrdersFilters: PropTypes.func.isRequired,
  currencyCode: PropTypes.string,
  dateRange: dateRangeTypes.isRequired,
  filterOptions: filterOptionTypes,
  handleSubmit: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  previousLocationWasReturnStatus: PropTypes.bool.isRequired,
  removeOrdersFilter: PropTypes.func.isRequired,
  requestOrders: PropTypes.func.isRequired,
  selectedFilters: PropTypes.any.isRequired,
  setOrdersFilters: PropTypes.func.isRequired,
  sortBy: sortByTypes.isRequired,
  updateOrdersDateRange: PropTypes.func.isRequired,
  updateSortBy: PropTypes.func.isRequired,
  userAccount: userAccountTypes,
  gamedayAccount: gamedayAccountTypes,
  userAllDepartments: PropTypes.arrayOf(userDepartmentTypes),
  userDepartments: userDepartmentTypes,
  user: userTypes,
  setGlobalSearchTerm: PropTypes.func.isRequired,
  setSortBy: PropTypes.func.isRequired,
  globalSearchTerm: PropTypes.string,
  globalSortBy: sortByTypes.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  setOriginatesFromReturnStatus: PropTypes.func.isRequired,
  clearSortBy: PropTypes.func.isRequired,
  resetPreviousLocation: PropTypes.func.isRequired,
  hasAuthCheckFinished: PropTypes.bool.isRequired,
  previousLocationWasReturnStep: PropTypes.bool.isRequired,
  previousLocationWasOrderDetails: PropTypes.bool.isRequired,
  navNewReturn: PropTypes.bool,
  setNavNewReturn: PropTypes.func.isRequired,
  setOrdersTableSearch: PropTypes.func.isRequired,
  clearOrdersTableSearch: PropTypes.func.isRequired,
  ordersTableSearch: PropTypes.object.isRequired,
  isGreaterChinaSalesOrg: PropTypes.bool.isRequired,
  generateOrderTrackingReport: PropTypes.func.isRequired,
  isGeneratingOrderTrackingReport: PropTypes.bool.isRequired,
  hasDownloadError: PropTypes.bool.isRequired,
  clearDownloadReportError: PropTypes.func.isRequired,
  archiveOrders: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired
};

OrderStatus.defaultProps = {
  billableDepartments: [],
  currencyCode: null,
  filterOptions: null,
  userAccount: null,
  gamedayAccount: null,
  userAllDepartments: null,
  userDepartments: null,
  user: null,
  globalSearchTerm: '',
  navNewReturn: false
};

const mapStateToProps = state => ({
  billableDepartments: billableDepartments(state),
  currencyCode: getCurrentAllotments(state)?.currencyCode || 'USD',
  dateRange: dateRange(state),
  filterOptions: filterOptions(state),
  hasAuthCheckFinished: hasAuthCheckFinished(state),
  isFetching: isFetching(state),
  isGreaterChinaSalesOrg: getIsGreaterChinaSalesOrg(state),
  previousLocationWasOrderDetails: previousLocationWasOrderDetails(state),
  previousLocationWasReturnStatus: previousLocationWasReturnStatus(state),
  previousLocationWasReturnStep: previousLocationWasReturnStep(state),
  selectedFilters: selectedFilters(state),
  sortBy: sortBy(state),
  userAccount: getUserAccount(state),
  userAllDepartments: selectDepartments(state),
  userDepartments: userDepartments(state),
  user: getUserData(state),
  globalSearchTerm: globalSearchTerm(state),
  globalSortBy: globalSortBy(state),
  navNewReturn: navNewReturn(state),
  gamedayAccount: getGamedayAccount(state),
  ordersTableSearch: ordersTableSearch(state),
  initialValues: {
    orderStatus__search__searchKey: ordersTableSearch(state).searchKey,
    orderStatus__search__input: ordersTableSearch(state).searchTerm
  },
  isGeneratingOrderTrackingReport: isGeneratingOrderTrackingReport(state),
  hasDownloadError: hasDownloadError(state),
  archiveOrders: archiveOrders(state)
});

const mapDispatchToProps = {
  addOrdersFilter,
  clearOrdersFilters,
  clearSortBy,
  removeOrdersFilter,
  requestOrders,
  resetPreviousLocation,
  setOrdersFilters,
  updateOrdersDateRange,
  updateSortBy,
  setGlobalSearchTerm,
  setSortBy,
  setOriginatesFromReturnStatus,
  setNavNewReturn,
  setOrdersTableSearch,
  clearOrdersTableSearch,
  generateOrderTrackingReport,
  clearDownloadReportError,
  archiveOrders,
  showToast
};

const OrderStatusWithSearchForm = reduxForm({
  form: 'orderSearchForm'
})(OrderStatus);

export default connect(mapStateToProps, mapDispatchToProps)(OrderStatusWithSearchForm);
